import classNames from 'classnames';
import { type CSSProperties, type ReactNode, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Icon from '../Icon';
import TextLink from '../TextLink';
import ImgClose from './close.png';
import {
  Backdrop,
  Content,
  Footer,
  Header,
  StyledModal,
  Wrapper,
} from './modalParts';

interface Props {
  children: ReactNode;
  className?: string;
  footer?: ReactNode;
  header: ReactNode;
  isOpen: boolean;
  onRequestClose: () => void;
  shouldCloseOnOverlayClick?: boolean;
  style?: CSSProperties;
  backdropZIndex?: number;
  invisibleBackdrop?: boolean;
  hideCancel?: boolean;
  cancelText?: string;
}

function Modal({
  children,
  className,
  footer,
  header,
  isOpen,
  onRequestClose,
  shouldCloseOnOverlayClick = true,
  style,
  backdropZIndex = 11000,
  invisibleBackdrop = false,
  hideCancel = false,
  cancelText = '取消操作',
}: Props) {
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen && shouldCloseOnOverlayClick) {
      onRequestClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    document.addEventListener('keydown', onKeyDown, false);
    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const onOverlayClick = () => {
    if (shouldCloseOnOverlayClick) onRequestClose();
  };
  const modal = (
    <Backdrop
      style={{
        zIndex: backdropZIndex,
        ...(!!invisibleBackdrop && {
          backgroundColor: 'transparent',
        }),
      }}
      onMouseDown={onOverlayClick}
    >
      <Wrapper
        aria-modal
        role="dialog"
        className={classNames('text-base', className)}
        style={style}
        onMouseDown={(e) => {
          if (shouldCloseOnOverlayClick) {
            e.stopPropagation();
          }
        }}
      >
        <StyledModal>
          <Header>
            {header}
            <Icon
              src={ImgClose}
              className="absolute right-0 cursor-pointer"
              onClick={onRequestClose}
            />
          </Header>
          <Content>{children}</Content>
          {!!footer && (
            <Footer>
              {footer}
              {!hideCancel && (
                <TextLink
                  scheme="light"
                  className="absolute left-0"
                  onClick={onRequestClose}
                >
                  {cancelText}
                </TextLink>
              )}
            </Footer>
          )}
        </StyledModal>
      </Wrapper>
    </Backdrop>
  );

  return isOpen ? ReactDOM.createPortal(modal, document.body) : null;
}

export default Modal;
